/**
 * Visibility-aware tab leader election using the Web Locks API.
 *
 * Why: every tab that subscribes to Momento topics counts as a billable
 * subscriber on each fan-out event. A user with three tabs open triples
 * realtime cost for zero product benefit — only one tab needs to own the
 * live subscriptions. Non-leader tabs receive events via BroadcastChannel.
 *
 * Why "visibility-aware":
 *   - The Web Lock callback would naturally hold leadership until the tab
 *     dies. That breaks down when the lock-holding tab is *hidden* and
 *     gets throttled or frozen by the browser — the lock is still held,
 *     visible siblings can't take over, and live subscriptions effectively
 *     stop for everyone.
 *   - We therefore release the lock when the tab is hidden for more than
 *     a short grace period, and re-request it when the tab becomes
 *     visible again. Whichever visible tab the browser grants the lock to
 *     becomes the active leader.
 *
 * Falls back to "leader = true" in browsers without `navigator.locks`
 * (Safari < 15.4 etc), preserving today's behavior.
 */

const LOCK_NAME = "slumbr.realtime.leader";
const CHANNEL_NAME = "slumbr.realtime.bus";
/** After becoming hidden, wait this long before releasing the lock. Lets
 *  the user briefly Alt-Tab without thrashing leadership. */
const HIDDEN_RELEASE_DELAY_MS = 15_000;

let leader = false;
let initialized = false;
let releaseHeld: (() => void) | null = null;
let hiddenReleaseTimer: ReturnType<typeof setTimeout> | null = null;
const leaderListeners = new Set<(isLeader: boolean) => void>();
const messageListeners = new Set<(payload: unknown) => void>();
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === "undefined") return null;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (ev) => {
    for (const listener of messageListeners) listener(ev.data);
  };
  return channel;
}

function setLeader(value: boolean) {
  if (leader === value) return;
  leader = value;
  for (const listener of leaderListeners) listener(value);
}

function isHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function attemptAcquire(): void {
  if (releaseHeld) return; // already holding or queued
  if (typeof navigator === "undefined" || !("locks" in navigator)) {
    setLeader(true);
    return;
  }

  void navigator.locks
    .request(LOCK_NAME, { mode: "exclusive" }, async () => {
      setLeader(true);
      await new Promise<void>((done) => {
        releaseHeld = done;
      });
      setLeader(false);
      releaseHeld = null;
    })
    .catch((err) => {
      // AbortError or rejection — ensure we don't think we're holding it.
      console.warn("[tab-leader] lock request rejected", err);
      releaseHeld = null;
    });
}

function releaseLeadership(): void {
  if (releaseHeld) {
    releaseHeld();
    releaseHeld = null;
  }
}

function onVisibilityChange() {
  if (isHidden()) {
    // Don't release immediately — user may flip tabs for a second. After
    // the grace period, drop leadership so a sibling visible tab can pick
    // it up. If we're STILL hidden when the timer fires, we release.
    if (hiddenReleaseTimer) clearTimeout(hiddenReleaseTimer);
    hiddenReleaseTimer = setTimeout(() => {
      hiddenReleaseTimer = null;
      if (isHidden()) releaseLeadership();
    }, HIDDEN_RELEASE_DELAY_MS);
  } else {
    if (hiddenReleaseTimer) {
      clearTimeout(hiddenReleaseTimer);
      hiddenReleaseTimer = null;
    }
    // We're visible — make sure we're either holding the lock or queued
    // for it. If a hidden sibling is currently holding the lock, our
    // request just sits in the browser's queue until it releases (which
    // it will, on its own visibility timer).
    attemptAcquire();
  }
}

function onPageHide() {
  releaseLeadership();
}

/** Idempotent initializer. Safe to call multiple times. */
export function ensureLeaderElection(): Promise<void> {
  if (!initialized) {
    initialized = true;
    if (typeof window !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("pagehide", onPageHide);
    }
    // Only request the lock on init if we're visible. Hidden tabs let
    // visible siblings win leadership; they'll request on becoming
    // visible.
    if (!isHidden()) attemptAcquire();
  }
  // Resolve quickly so callers can wire dependent subscriptions. The
  // actual leadership grant may happen synchronously (single tab) or
  // later (queued behind another tab) — `subscribeToLeaderChange`
  // notifies callers of every state change.
  return Promise.resolve();
}

export function isTabLeader(): boolean {
  return leader;
}

export function subscribeToLeaderChange(listener: (isLeader: boolean) => void): () => void {
  leaderListeners.add(listener);
  // Push current value so subscribers don't miss the initial state.
  listener(leader);
  return () => leaderListeners.delete(listener);
}

export interface BroadcastEnvelope {
  type: string;
  payload: unknown;
}

/** Leader: broadcast a payload to all sibling tabs in this origin. */
export function broadcastFromLeader(envelope: BroadcastEnvelope): void {
  getChannel()?.postMessage(envelope);
}

/** Follower (or leader echoing): receive payloads from the leader. */
export function subscribeToBroadcasts(
  listener: (envelope: BroadcastEnvelope) => void
): () => void {
  const wrapped = (payload: unknown) => {
    const env = payload as BroadcastEnvelope;
    if (env && typeof env.type === "string") listener(env);
  };
  messageListeners.add(wrapped);
  getChannel();
  return () => {
    messageListeners.delete(wrapped);
  };
}
